import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { FilterPipelineFilter } from 'record-it-components/hooks/useFilterPipeline/useFilterPipeline';
import { SearchableSelect } from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { User } from '../../classes/entities/User/userTypes';
import { UserUtils } from '../../classes/entities/User/UserUtils';
import { useSelectEntityItems } from '../../hooks/entity/useSelectEntityItems/useSelectEntityItems';

/**
 * A filter for filtering entities by the userId.
 * When a value gets select the filter will navigate to a new url
 *
 * This filter needs to be recreated each time the userId of the url changes, since it has no internal state, because the state is in the url
 */
export function createUserIdUrlFilter({
  users,
  createUrl,
  currentUserId
}: CreateUserIdUrlFilterOptions): FilterPipelineFilter<
  { userId: string },
  string
> {
  return {
    defaultValue: currentUserId,
    filter: (item, value) => {
      return item.userId === value;
    },
    Component: ({ value }) => {
      const { t } = useTranslation();

      const navigate = useNavigate();

      const handleValueChanged = useEventCallback((userId: string) => {
        navigate(createUrl(userId));
      });

      const useSelectEntityItemsReturnValue = useSelectEntityItems({
        value,
        entities: users,
        entityNotFoundText: t('filters.userIdUrlFilter.unknownUser'),
        getDisplayTextOfEntity: UserUtils.displayTextGetter,
        getKeyOfEntity: UserUtils.keyGetter,
        getValueOfEntity: (ug) => ug.id,
        onValueChanged: handleValueChanged
      });

      return (
        <SearchableSelect
          value={useSelectEntityItemsReturnValue.selectedItem}
          items={useSelectEntityItemsReturnValue.items}
          onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged}
          label={t('filters.userIdUrlFilter.label')}
          getDisplayTextOfItem={
            useSelectEntityItemsReturnValue.getDisplayTextOfItem
          }
          getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem}
          data-testid="userIdUrlFilter.Select"
        ></SearchableSelect>
      );
    }
  };
}

export type UserIdUrlFilter = FilterPipelineFilter<{ userId: string }, string>;

export type CreateUserIdUrlFilterOptions = {
  users: Array<User>;
  currentUserId: string;
  createUrl: (userId: string) => string;
};
